/**
 * FranchiseReconciliationPage — `/franchise/reconciliation`.
 *
 * Lists DISPUTED + PENDING revenue snapshots for a franchise so the operator
 * can act on them via the controller's reconcile / settle / dispute endpoints
 * (`POST /v1/franchise/revenue/snapshots/:id/{reconcile|settle|dispute}`).
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listFranchises,
  listRevenueSnapshots,
  reconcileSnapshot,
  settleSnapshot,
  type RevenueSnapshotStatus,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const STATUSES: readonly RevenueSnapshotStatus[] = ['PENDING', 'RECONCILED', 'DISPUTED', 'SETTLED'];

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function FranchiseReconciliationPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [statusFilter, setStatusFilter] = useState<RevenueSnapshotStatus | ''>('DISPUTED');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchises = useQuery({
    queryKey: ['franchise', 'recon', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const snaps = useQuery({
    queryKey: ['franchise', 'recon', 'snaps', franchiseId, statusFilter],
    queryFn: () =>
      listRevenueSnapshots(franchiseId, {
        pageSize: 50,
        ...(statusFilter === '' ? {} : { status: statusFilter as RevenueSnapshotStatus }),
      }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const reconcile = useMutation({
    mutationFn: (id: string) => {
      const ledgerRef = window.prompt('Ledger reference?') ?? '';
      if (ledgerRef.length === 0) throw new Error('Cancelled');
      return reconcileSnapshot(id, ledgerRef);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'recon', 'snaps'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const settle = useMutation({
    mutationFn: (id: string) => settleSnapshot(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'recon', 'snaps'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied =
    snaps.isError && snaps.error instanceof ApiError &&
    (snaps.error.status === 401 || snaps.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reconciliation"
        description="Snapshots awaiting reconciliation, dispute resolution, or settlement."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
          {franchises.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm min-w-[260px]"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RevenueSnapshotStatus | '')}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise.
        </p>
      ) : snaps.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseRevenue/read</code> required.
        </DeferredNotice>
      ) : snaps.isError ? (
        <DeferredNotice title="Service unreachable">{(snaps.error as Error).message}</DeferredNotice>
      ) : (snaps.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No snapshots match this filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-right px-4 py-2">Commission</th>
                <th className="text-left px-4 py-2">Ledger ref</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {snaps.data?.items.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs text-muted">{s.date}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(s.totalRevenue)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(s.commissionAmount)}
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px] text-muted">
                    {s.reconciledRef ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {(s.status === 'PENDING' || s.status === 'DISPUTED') ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          reconcile.mutate(s.id);
                        }}
                        disabled={reconcile.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Reconcile →
                      </button>
                    ) : null}
                    {s.status === 'RECONCILED' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          settle.mutate(s.id);
                        }}
                        disabled={settle.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Settle →
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
